import React from "react";
import {
  AccountBalanceOutlined,
  BadgeOutlined,
  CalendarMonthOutlined,
  CardMembershipOutlined,
  InsertDriveFileOutlined,
  LibraryBooksOutlined,
  NightShelterOutlined,
  ReceiptLongOutlined,
} from "@mui/icons-material";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { routes } from "@routes";
import { TNavigationAreaPath } from "@components/Areas/NavigationArea/NavigationArea";
import { useAuth, useSession } from "@hooks";
import {
  AreaPage,
  ConfirmationPage,
  Test,
  FastCheckinPage,
  HomePage,
  LoginPage,
  RegisterPage,
  ReservationPage,
  ChangePasswordWidget,
  FCIPage,
} from "@pages";
import {
  AccomodationsArea,
  BankAccountsArea,
  BillsArea,
  BookletsArea,
  CustomerInfosArea,
  IndexArea,
  LeasesArea,
  OccupationRightsArea,
  ReservationsArea,
  //NotificationsArea
} from "@components/Areas";
import { ReservationPageWrapper } from "@components/Reservation/ReservationPageWrapper";

type _RoutesProps = {
  //
};

const languagesVarsCodes = [
  "LANG_neobooking_area_my_reservations_title", //Mes réservations
  "LANG_neobooking_area_my_reservations_description", //Consultez et éditez les réservations, les préstations et les participant
  "LANG_neobooking_area_my_informations_title", //Coordonnées
  "LANG_neobooking_area_my_informations_description", //Mettez à jour vos informations personnelles et votre mot de passe
  "LANG_neobooking_area_booklets_title", //Brochures
  "LANG_neobooking_area_booklets_description", //Demandez les brochures des différents hébergements et établissements
  "LANG_neobooking_area_accommodations_title", //Mes hébergements
  "LANG_neobooking_area_accommodations_description", //Consultez et gérez vos hébergements, établissements et préstations
  "LANG_neobooking_area_bills_title", //Factures
  "LANG_neobooking_area_bills_description", //Consultez les factures, les paiements et les versements
  "LANG_neobooking_area_bank_account_title", //Comptes bancaires
  "LANG_neobooking_area_bank_account_description", //Consultez, ajoutez et supprimez des comptes bancaires
  "LANG_neobooking_area_reservations_title", //Réservations
  "LANG_neobooking_area_reservations_description", //Consultez et éditez les réservations, les préstations et les participant
  "LANG_neobooking_area_leases_title", //Baux
  "LANG_neobooking_area_leases_description", //Consultez et téléchargez les contrats de baux entre vous et le locataires
  "LANG_neobooking_area_occupation_rights_title", //Crédits séjour
  "LANG_neobooking_area_occupation_rights_description", //Visualisez l'ensemble des crédits séjours consommés
  "LANG_neobooking_area_customer_title", //Mon espace client
  "LANG_neobooking_area_customer_description", //Bienvenue dans votre espace client où vous pouvez consulter et modifier vos informations personnelles, gérer vos réservations et faire demander une brochure.
  "LANG_neobooking_area_partner_title", //Mon espace partenaire
  "LANG_neobooking_area_partner_description", //Bienvenue dans votre espace partenaire où vous pouvez consulter et modifier vos informations personnelles, suivre et gérer vos hébergements et factures.
  "LANG_neobooking_area_owner_title", //Mon espace propriétaire
  "LANG_neobooking_area_owner_description", //Bienvenue dans votre espace propriétaire où vous pouvez consulter et modifier vos informations personnelles, suivre et gérer vos hébergements et factures.
];

const _Routes: React.FC<_RoutesProps> = () => {
  /* Hooks */
  const { getLanguageVarValue } = useSession();

  const userPaths: TNavigationAreaPath[] = [
    {
      name: getLanguageVarValue("LANG_neobooking_area_my_reservations_title"),
      description: getLanguageVarValue(
        "LANG_neobooking_area_my_reservations_description"
      ),
      to: routes.customerArea_reservations,
      order: 1,
      muiIcon: CalendarMonthOutlined,
    },
    {
      name: getLanguageVarValue("LANG_neobooking_area_my_informations_title"),
      description: getLanguageVarValue(
        "LANG_neobooking_area_my_informations_description"
      ),
      to: routes.customerArea_info,
      order: 2,
      muiIcon: BadgeOutlined,
    },
    {
      name: getLanguageVarValue("LANG_neobooking_area_booklets_title"),
      description: getLanguageVarValue(
        "LANG_neobooking_area_booklets_description"
      ),
      to: routes.customerArea_booklets,
      order: 3,
      muiIcon: LibraryBooksOutlined,
    },
    /*{
      name: "Notifications",
      description: "Choisissez vos préférences de notification et la façon dont vous souhaitez être contacté",
      to: routes.customerArea_notifications,
      order: 4,
      muiIcon: NotificationsActiveOutlined
    }*/
    /*{
      name: "Avantages fidélité",
      description: "Vous disposez de 0€ de crédits et coupon de parrainage. Voir plus",
      to: routes.customerArea_fidelity,
      order: 4,
      muiIcon: CardGiftcardOutlined
    }*/
  ];

  const partnerPaths: TNavigationAreaPath[] = [
    {
      name: getLanguageVarValue("LANG_neobooking_area_accommodations_title"),
      description: getLanguageVarValue(
        "LANG_neobooking_area_accommodations_description"
      ),
      to: routes.partnerArea_accommodations,
      order: 1,
      muiIcon: NightShelterOutlined,
    },
    {
      name: getLanguageVarValue("LANG_neobooking_area_my_informations_title"),
      description: getLanguageVarValue(
        "LANG_neobooking_area_my_informations_description"
      ),
      to: routes.partnerArea_info,
      order: 2,
      muiIcon: BadgeOutlined,
    },
    {
      name: getLanguageVarValue("LANG_neobooking_area_bills_title"),
      description: getLanguageVarValue(
        "LANG_neobooking_area_bills_description"
      ),
      to: routes.partnerArea_bills,
      order: 3,
      muiIcon: ReceiptLongOutlined,
    },
    {
      name: getLanguageVarValue("LANG_neobooking_area_bank_account_title"),
      description: getLanguageVarValue(
        "LANG_neobooking_area_bank_account_description"
      ),
      to: routes.partnerArea_bankAccounts,
      order: 4,
      muiIcon: AccountBalanceOutlined,
    },
    /*{
      name: "Notifications",
      description: "Choisissez vos préférences de notification et la façon dont vous souhaitez être contacté",
      to: routes.partnerArea_notifications,
      order: 4,
      muiIcon: NotificationsActiveOutlined
    }*/
  ];

  const ownerPaths: TNavigationAreaPath[] = [
    {
      name: getLanguageVarValue("LANG_neobooking_area_reservations_title"),
      description: getLanguageVarValue(
        "LANG_neobooking_area_reservations_description"
      ),
      to: routes.ownerArea_reservations,
      order: 1,
      muiIcon: CalendarMonthOutlined,
    },
    {
      name: getLanguageVarValue("LANG_neobooking_area_my_informations_title"),
      description: getLanguageVarValue(
        "LANG_neobooking_area_my_informations_description"
      ),
      to: routes.ownerArea_info,
      order: 2,
      muiIcon: BadgeOutlined,
    },
    {
      name: getLanguageVarValue("LANG_neobooking_area_bills_title"),
      description: getLanguageVarValue(
        "LANG_neobooking_area_bills_description"
      ),
      to: routes.ownerArea_bills,
      order: 3,
      muiIcon: ReceiptLongOutlined,
    },
    {
      name: getLanguageVarValue("LANG_neobooking_area_bank_account_title"),
      description: getLanguageVarValue(
        "LANG_neobooking_area_bank_account_description"
      ),
      to: routes.ownerArea_bankAccounts,
      order: 4,
      muiIcon: AccountBalanceOutlined,
    },
    {
      name: getLanguageVarValue("LANG_neobooking_area_leases_title"),
      description: getLanguageVarValue(
        "LANG_neobooking_area_leases_description"
      ),
      to: routes.ownerArea_leases,
      order: 5,
      muiIcon: InsertDriveFileOutlined,
    },
    {
      name: getLanguageVarValue("LANG_neobooking_area_occupation_rights_title"),
      description: getLanguageVarValue(
        "LANG_neobooking_area_occupation_rights_description"
      ),
      to: routes.ownerArea_occupationRights,
      order: 6,
      muiIcon: CardMembershipOutlined,
    },
    /*{
      name: "Notifications",
      description: "Choisissez vos préférences de notification et la façon dont vous souhaitez être contacté",
      to: routes.ownerArea_notifications,
      order: 4,
      muiIcon: NotificationsActiveOutlined
    }*/
  ];

  return (
    <Routes>
      <Route path={routes.home} element={<HomePage />} />
      <Route path={routes.fastcheckin} element={<FCIPage />} />
      {/* Reservation Route */}
      <Route path={routes.reservation} element={<ReservationPageWrapper />} />
      <Route path={"/sandbox"} element={<ChangePasswordWidget />} />
      {/* Confirmation Route */}
      <Route path={routes.confirmation} element={<ConfirmationPage />} />
      {/* Customer Area Routes */}
      <Route
        path={routes.customerArea}
        element={
          <RequiredAuth clientTypeIds={[1, 2, 3, 4, 5, 6, 7]}>
            <AreaPage
              title={getLanguageVarValue("LANG_neobooking_area_customer_title")}
              navigationPaths={userPaths}
              home={routes.customerArea}
            />
          </RequiredAuth>
        }
      >
        <Route
          index
          element={
            <IndexArea
              title={getLanguageVarValue(
                "LANG_neobooking_area_customer_description"
              )}
              navigationPaths={userPaths}
            />
          }
        />
        <Route
          path={routes.customerArea_reservations}
          element={<ReservationsArea />}
        />
        <Route
          path={routes.customerArea_info}
          element={<CustomerInfosArea />}
        />
        <Route path={routes.customerArea_booklets} element={<BookletsArea />} />
        {/*<Route path={routes.customerArea_notifications} element={<NotificationsArea />} />*/}
        {/*<Route path={routes.customerArea_fidelity} element={<LoyaltyAdvantagesArea />} />*/}
      </Route>
      {/* Partner Area Routes */}
      <Route
        path={routes.partnerArea}
        element={
          <RequiredAuth clientTypeIds={[2]}>
            <AreaPage
              title={getLanguageVarValue("LANG_neobooking_area_partner_title")}
              navigationPaths={partnerPaths}
              home={routes.partnerArea}
            />
          </RequiredAuth>
        }
      >
        <Route
          index
          element={
            <IndexArea
              title={getLanguageVarValue(
                "LANG_neobooking_area_partner_description"
              )}
              navigationPaths={partnerPaths}
            />
          }
        />
        <Route
          path={routes.partnerArea_accommodations}
          element={<AccomodationsArea />}
        />
        <Route path={routes.partnerArea_info} element={<CustomerInfosArea />} />
        <Route path={routes.partnerArea_bills} element={<BillsArea />} />
        <Route
          path={routes.partnerArea_bankAccounts}
          element={<BankAccountsArea />}
        />
        {/*<Route path={routes.partnerArea_notifications} element={<NotificationsArea />} />*/}
      </Route>
      {/* Owner / Propriétaire Routes */}
      <Route
        path={routes.ownerArea}
        element={
          <RequiredAuth clientTypeIds={[4, 5]}>
            <AreaPage
              title={getLanguageVarValue("LANG_neobooking_area_owner_title")}
              navigationPaths={ownerPaths}
              home={routes.ownerArea}
            />
          </RequiredAuth>
        }
      >
        <Route
          index
          element={
            <IndexArea
              title={getLanguageVarValue(
                "LANG_neobooking_area_owner_description"
              )}
              navigationPaths={ownerPaths}
            />
          }
        />
        <Route
          path={routes.ownerArea_reservations}
          element={<ReservationsArea />}
        />
        <Route path={routes.ownerArea_info} element={<CustomerInfosArea />} />
        <Route path={routes.ownerArea_bills} element={<BillsArea />} />
        <Route
          path={routes.ownerArea_bankAccounts}
          element={<BankAccountsArea />}
        />
        <Route path={routes.ownerArea_leases} element={<LeasesArea />} />
        <Route
          path={routes.ownerArea_occupationRights}
          element={<OccupationRightsArea />}
        />
        {/*<Route path={routes.ownerArea_notifications} element={<NotificationsArea />} />*/}
      </Route>
      {/* Auth Routes */}
      <Route
        path={routes.login}
        element={
          <RedirectIfAuth>
            <LoginPage />
          </RedirectIfAuth>
        }
      />
      <Route
        path={routes.register}
        element={
          <RedirectIfAuth>
            <RegisterPage />
          </RedirectIfAuth>
        }
      />
      <Route path="/test" element={<Test />} />
      {/* Not Found Route */}
      <Route path="*" element={<Navigate to={routes.home} />} />
    </Routes>
  );
};

export default _Routes;

//Redirect user to "/" (index) if logged
const RedirectIfAuth = ({ children }: { children: JSX.Element }) => {
  const { user, loading } = useAuth();
  const location: any = useLocation();
  if (!loading) {
    if (user) {
      // Redirect them to the /login page, but save the current location they were
      // trying to go to when they were redirected. This allows us to send them
      // along to that page after they login, which is a nicer user experience
      // than dropping them off on the home page.
      return (
        <Navigate
          to={location.state ? location.state.from.pathname : routes.home}
          state={{ from: location }}
          replace
        />
      );
    }
  }
  return children;
};

//Redirect user to "/login" (login page) if not logged
const RequiredAuth = ({
  children,
  clientTypeIds,
}: {
  children: JSX.Element;
  clientTypeIds: number[];
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (!loading) {
    if (!user) {
      // Redirect them to the /login page, but save the current location they were
      // trying to go to when they were redirected. This allows us to send them
      // along to that page after they login, which is a nicer user experience
      // than dropping them off on the home page.
      return <Navigate to={routes.login} state={{ from: location }} replace />;
    }
    if (user && !clientTypeIds.includes(user.clientType?.id!!)) {
      return <Navigate to={routes.home} />;
    }
  }
  return children;
};
